import * as React from "react";
import { Combobox, Button, Spinner, InputGroup, Input } from "@/components/ui";
import { ChevronDown, ChevronUp, Search, Check, X, List, Grid2x2 } from "lucide-react";
import { debounce } from "lodash";

import * as DTO from "../../../../interfaces";
import { useAccountSettings, URLS, useCategories, useLocalStorage, useSocialCoachSession, STORAGE_KEYS } from "@/utils";
import { PromptServices } from "@/services";

import { UploadModal } from "./UploadModal";

import { SearchFilter, WebComponents } from "@/interfaces";
import { getDownloadURL } from "firebase/storage";
import { useMemo } from "react";
import { PromptCategoryCards } from "@/components/PromptCategoryCards";
import PromptsList from "./PromptList";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

const Prompts: React.FC = () => {
  const history = useHistory();
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const writePermission = hasWritePermission(WebComponents.PROMPTS);
  const writeCampaignPermission = hasWritePermission(WebComponents.CAMPAIGNS);

  const [displayLoadingSection, setDisplayLoadingSection] = React.useState(true);
  const [promptsBulkDic, setPromptsBulkDic] = React.useState<{ [key: string]: DTO.PromptPreview }>();

  const [refresh, setRefresh] = React.useState(false);
  const [uploadEnable, setUploadEnable] = React.useState(true);

  const [uploading, setUploading] = React.useState(false);
  const [bulkRequest, setBulkRequest] = React.useState<DTO.PromptBulkRequest>();

  React.useEffect(() => {
    if (!uploadEnable) {
      Object.values(promptsBulkDic!).map(({ uploadTask }) => {
        if (uploadTask) {
          uploadTask.cancel();
        }
      });
      setPromptsBulkDic(undefined);
      setBulkRequest(undefined);
    }
  }, [uploadEnable]);

  React.useEffect(() => {
    if (uploadEnable && bulkRequest) {
      setUploadEnable(false);
      PromptServices.createBulk(bulkRequest).finally(() => {
        setPromptsBulkDic(undefined);
        setRefresh(true);
        setBulkRequest(undefined);
      });
    } else if (!uploadEnable && bulkRequest) {
      setPromptsBulkDic(undefined);
      setRefresh(true);
      setBulkRequest(undefined);
    }
  }, [bulkRequest]);

  React.useEffect(() => {
    if (uploading && uploadEnable) {
      uploadPrompts();
      setUploading(false);
    }
  }, [promptsBulkDic]);

  const createCampaign = () => {
    localStorage.removeItem(STORAGE_KEYS.COACH_NEW_CAMPAIGN_STEP);
    localStorage.removeItem(STORAGE_KEYS.COACH_CAMPAIGN);
    history.push(URLS.coach.newCampaigns);
  };

  const addPromptHandler = () => {
    history.push(URLS.coach.newPrompt, {
      deliveryMode: "LIBRARY",
    });
  };

  const uploadPrompts = (): void => {
    if (promptsBulkDic) {
      const uploadTaskPromises = Object.values(promptsBulkDic!).map(({ uploadTask }) => {
        return uploadTask;
      });
      if (uploadTaskPromises) {
        Promise.all(uploadTaskPromises).then(values => {
          const imagesPromises = Object.values(values).map(uploadTask => {
            return getDownloadURL(uploadTask!.ref);
          });

          Promise.all(imagesPromises).then(values2 => {
            const prompts: DTO.PromptRequest[] = [];
            if (promptsBulkDic) {
              Object.values(promptsBulkDic!).map(
                async ({ promptId, categoryIds, title, message, richMessage, status, deliveryMode, post }, index) => {
                  const url = values2[index];

                  const newPost: DTO.MediaGroup = {
                    media: post?.media || [],
                    mediaGroupId: undefined,
                    type: post!.type,
                  };

                  newPost!.media[0]!.uri = url;
                  prompts.push({
                    promptId,
                    coachId,
                    title,
                    message,
                    richMessage,
                    status,
                    deliveryMode,
                    post: newPost,
                    categoryIds,
                  });
                }
              );
              const request: DTO.PromptBulkRequest = {
                coachId: coachId!,
                deliveryMode: "LIBRARY",
                promptList: prompts,
              };
              setBulkRequest(request);
            }
          });
        });
      }
    }
  };
  const [promptList, setPromptList] = React.useState<DTO.Prompt[]>([]);

  type ViewType = { value: "LIST" | "CARD"; label: string; icon: any };
  const viewTypes: ViewType[] = [
    { value: "LIST", label: "List View", icon: <List /> },
    { value: "CARD", label: "Card View", icon: <Grid2x2 /> },
  ];
  const [selectedViewTypeValue, setSelectedViewTypeValue] = useLocalStorage("libraryViewType", viewTypes[0].value);
  const selectedViewType = React.useMemo(() => {
    return viewTypes.find(viewType => viewType.value === selectedViewTypeValue) || viewTypes[0];
  }, [selectedViewTypeValue]);

  const [loading, setLoading] = React.useState<boolean>(true);

  const [searchTitle, setSearchTitle] = React.useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = React.useState<DTO.Category[]>([]);

  const { categories } = useCategories("LIBRARY");

  const search = (
    searchContent: string | null = searchTitle,
    categories: DTO.Category[] | null = selectedCategories
  ) => {
    setLoading(true);

    const categoryIds: string[] = (categories?.map(category => category.categoryId)?.filter(Boolean) as string[]) || [];
    const filterBody: SearchFilter = {
      title: searchContent,
      status: categoryIds,
    };

    PromptServices.library(coachId!, "createdDate,DESC", filterBody)
      .then(response => {
        setPromptList(response.content);
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(search, []);
  React.useEffect(search, [selectedCategories, searchTitle]);
  React.useEffect(() => {
    if (refresh) {
      search();
    }
  }, [refresh]);

  const delayedQuery = React.useRef(debounce(setSearchTitle, 500)).current;
  const onChange = (e: any) => {
    delayedQuery(e.target.value);
  };

  const content = useMemo(() => {
    if (loading) {
      return <Spinner className="size-12 self-center mt-25" />;
    } else if (selectedViewTypeValue === "LIST") {
      return <PromptsList categories={categories} promptList={promptList} />;
    } else {
      return <PromptCategoryCards categories={categories} promptList={promptList} />;
    }
  }, [selectedViewTypeValue, categories, promptList, loading]);

  return (
    <div className="flex-auto flex flex-col gap-6">
      <div className="flex items-center flex-wrap justify-between gap-4">
        <div className="flex gap-4 items-center flex-wrap flex-auto">
          <InputGroup
            className="max-w-80 w-auto min-w-50"
            control={controlProps => <Input {...controlProps} placeholder="Search..." onChange={onChange} />}
            append={<Search />}
          />
          {categories && categories?.length > 0 && (
            <Combobox
              items={categories}
              value={selectedCategories}
              itemToStringValue={category => category.name}
              itemToStringLabel={category => category.name}
              onValueChange={value => setSelectedCategories(value)}
              placeholder="Filter by Categories"
              emptyText="No Categories found."
              showClear
              searchable
              className="max-w-80 min-w-50"
              maxChips="auto"
              multiple
            />
          )}
          <Combobox
            items={viewTypes}
            value={selectedViewType}
            onValueChange={value => setSelectedViewTypeValue((value || selectedViewType)?.value)}
            isItemEqualToValue={(viewType, selected) => viewType.value === selected?.value}
            placeholder="Filter by View Type"
            className="max-w-40 min-w-40"
          />
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {writeCampaignPermission && (
            <Button variant="outline" onClick={createCampaign} children="Create a Campaign" />
          )}
          {writePermission && (
            <UploadModal
              trigger={<Button variant="outline" children="Upload Files" />}
              onUploaded={submitPrompts => {
                setUploadEnable(true);
                setUploading(true);
                setPromptsBulkDic(submitPrompts);
              }}
              modalOpened={() => setRefresh(false)}
              submittingFiles={submitPrompts => {
                if (uploadEnable) {
                  setPromptsBulkDic(submitPrompts);
                }
              }}
            />
          )}
          {writePermission && <Button onClick={addPromptHandler} children="New Prompt" />}
        </div>
      </div>

      {content}

      {promptsBulkDic && (
        <div className="bg-white max-h-115 overflow-x-auto fixed bottom-0 right-2 m-0 p-0 rounded-lg w-75 shadow-sm flex flex-col">
          <div className="bg-accent py-3">
            <Button
              variant="ghost"
              size="lg"
              className="font-semibold w-full text-accent-foreground *:bg-transparent"
              onClick={() => setDisplayLoadingSection(!displayLoadingSection)}
            >
              <div className="text-center basis-full">{`Uploading ${Object.keys(promptsBulkDic).length} ${pluralize(
                "File",
                Object.keys(promptsBulkDic).length
              )}`}</div>
              {displayLoadingSection ? <ChevronDown className="size-6" /> : <ChevronUp className="size-6" />}
            </Button>
          </div>
          <Button variant="link" className="self-end" onClick={() => setUploadEnable(false)} children="Cancel" />

          {displayLoadingSection &&
            Object.values(promptsBulkDic).map((prompt, index) => {
              let icon = <Spinner className="size-6 min-size-6 min-w-6 min-h-6" />;
              if (prompt.loadStatus === "SUBMITTED") {
                icon = (
                  <Check
                    className="size-6 min-size-6 min-w-6 min-h-6 bg-primary text-white rounded-full p-1"
                    strokeWidth={3}
                  />
                );
              } else if (prompt.loadStatus === "FAILED") {
                icon = (
                  <X
                    className="size-6 min-size-6 min-w-6 min-h-6 bg-destructive text-white rounded-full p-1"
                    strokeWidth={3}
                  />
                );
              }
              return (
                <div key={index} className="py-4 px-5 h-15 flex flex-row items-center justify-between gap-4">
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap">{prompt.title}</div>
                  {icon}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default Prompts;
